import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { eventsApi, zonesApi, checkinApi } from '../api';
import { playSound } from '../utils/audio';

export default function CheckinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const eventId = searchParams.get('event');
  const zoneId = searchParams.get('zone');

  const [state, setState] = useState('loading'); // loading | confirm | success | error | inactive | auto_checkin
  const [event, setEvent] = useState(null);
  const [zone, setZone] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const autoCheckinDone = useRef(false);

  useEffect(() => {
    if (!eventId || !zoneId) {
      setState('error');
      setResult({ message: 'Neplatný QR kód – chybí parametry MU nebo zóny.' });
      return;
    }

    async function loadData() {
      try {
        const [eventRes, zoneRes, statusRes] = await Promise.all([
          eventsApi.get(eventId),
          zonesApi.get(zoneId),
          checkinApi.status(eventId, zoneId),
        ]);

        if (eventRes.data && eventRes.data.active === 0) {
          setEvent(eventRes.data);
          setZone(zoneRes.data);
          setState('inactive');
          return;
        }

        setEvent(eventRes.data);
        setZone(zoneRes.data);
        setCurrentStatus(statusRes.data);

        if (zoneRes.data.auto_checkin === 1 && !autoCheckinDone.current) {
          autoCheckinDone.current = true;
          setState('auto_checkin');
          const lastStatus = statusRes.data;
          const autoAction = (lastStatus?.status === 'IN' && lastStatus?.zone_id === zoneId) ? 'OUT' : 'IN';
          try {
            const autoRes = await checkinApi.perform({ event_id: parseInt(eventId), zone_id: zoneId, action: autoAction });
            playSound(autoAction);
            setResult(autoRes.data);
            setState('success');
          } catch (err) {
            setResult({ message: err.response?.data?.error || 'Chyba při automatickém check-inu.' });
            setState('error');
          }
          return;
        }

        setState('confirm');
      } catch (err) {
        setState('error');
        setResult({ message: 'Nepodařilo se načíst informace o MU nebo zóně.' });
      }
    }

    loadData();
  }, [eventId, zoneId]);

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      const res = await checkinApi.perform({ event_id: parseInt(eventId), zone_id: zoneId, action });
      playSound(action);
      setResult(res.data);
      setState('success');
    } catch (err) {
      setResult({ message: err.response?.data?.error || 'Chyba při zpracování požadavku.' });
      setState('error');
    } finally {
      setActionLoading(false);
    }
  };

  const goHome = () => navigate('/');

  if (state === 'loading' || state === 'auto_checkin') {
    return (
      <div className="container" style={{ paddingTop: 48 }}>
        <div className="text-center">
          <div className="spinner" />
          <p className="text-muted">{state === 'auto_checkin' ? 'Automaticky zaznamenávám…' : 'Načítám informace o zóně…'}</p>
        </div>
      </div>
    );
  }

  if (state === 'inactive') {
    return (
      <div className="container" style={{ paddingTop: 48 }}>
        <div className="card text-center">
          <div className="result-icon">🔒</div>
          <div className="result-title" style={{ color: '#888' }}>MU byla ukončena</div>
          <p className="result-message" style={{ color: '#666' }}>
            <strong>{event?.name}</strong> je označena jako neaktivní. Check-in není možný.
          </p>
          <button className="btn btn-outline btn-full mt-16" onClick={goHome}>Zpět na rozcestník</button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="container" style={{ paddingTop: 48 }}>
        <div className="card text-center">
          <div className="result-icon">❌</div>
          <div className="result-title">Chyba</div>
          <p className="result-message">{result?.message || 'Nastala neznámá chyba.'}</p>
          <button className="btn btn-primary btn-full" onClick={goHome}>Zpět na rozcestník</button>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    const isIn = result?.status === 'IN';
    return (
      <div className="container" style={{ paddingTop: 48 }}>
        <div className="card text-center">
          <div className="result-icon">{isIn ? '✅' : '👋'}</div>
          <div className="result-title" style={{ color: isIn ? 'var(--green)' : 'var(--gray)' }}>
            {isIn ? 'Příchod zaznamenán' : 'Odchod zaznamenán'}
          </div>
          <p className="result-message">{result.message}</p>
          {zone && (
            <div className="alert alert-info mb-16" style={{ textAlign: 'left' }}>
              <strong>Zóna:</strong> {zone.name}<br />
              {event && <><strong>MU:</strong> {event.name}</>}
            </div>
          )}
          <button className="btn btn-primary btn-full" onClick={goHome}>Zpět na rozcestník</button>
          <button className="btn btn-outline btn-full mt-8" onClick={() => { setResult(null); setState('confirm'); }}>
            Provést další akci v této zóně
          </button>
        </div>
      </div>
    );
  }

  // confirm
  const isCurrentlyIn = currentStatus?.status === 'IN';
  const isInThisZone = isCurrentlyIn && currentStatus?.zone_id === zoneId;

  return (
    <div>
      <div className="header">
        <div className="brand">
          <img src="/logo.svg" width="38" height="38" alt="TEČČKa" />
          <div>
            <div className="header-title">TEČČKa</div>
            <div className="header-subtitle">{event?.name || 'MU'}</div>
          </div>
        </div>
        <button className="icon-btn" onClick={goHome}>Zpět</button>
      </div>

      <div className="container" style={{ paddingTop: 24 }}>
        <div className="card">
          <h2 className="mb-8">Zóna: {zone?.name}</h2>
          {event && <p className="text-muted mb-16" style={{ fontSize: '0.9rem' }}>MU: {event.name}</p>}

          {isCurrentlyIn && !isInThisZone && (
            <div className="alert alert-warning mb-16">
              Nyní jste v zóně <strong>{currentStatus.zone_name}</strong>. Příchodem sem budete z předchozí automaticky odhlášeni.
            </div>
          )}
          {isInThisZone && <div className="alert alert-success mb-16">Aktuálně jste přihlášeni v této zóně.</div>}
          {!isCurrentlyIn && <div className="alert alert-info mb-16">Nejste přihlášeni v žádné zóně.</div>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button className="btn btn-success btn-full btn-xl" onClick={() => handleAction('IN')} disabled={actionLoading}>
            {actionLoading ? '...' : '✅ Příchod'}
          </button>
          <button className="btn btn-outline btn-full btn-xl" onClick={() => handleAction('OUT')} disabled={actionLoading}
            style={{ borderColor: 'var(--gray)', color: 'var(--gray)' }}>
            {actionLoading ? '...' : '👋 Odchod'}
          </button>
        </div>

        <button className="btn btn-outline btn-full mt-16" onClick={goHome}>Zrušit</button>
      </div>
    </div>
  );
}
